import * as _ from "lodash"
import { EvalFunction, parse, SymbolNode, MathNode } from "mathjs";

import {
    IdsMetric,
    IdsRow,
    IMetricDataSet, MetricAxisType, MetricRowAggType
} from "./PA.MetricDataSet.IF";

/** Use these options to define how the ComputedMetric instance will calculate metrics */
export interface IComputedMetricCreateOptions {
    // Required to for calculation ordering
    /** List all metrics required to calculate this metric */
    requiredMetricsIds?: string[]
    /** List all attributes required to calculate this metric */
    requiredAttributeIds?: string[]

    // For Formula Driven Computed Metrics
    // this will create a computed metric using this metricFormula if it's provided. will also define setCOmputedValuesAllRows using this formula
    //  if it's not set, then you must define getComputedValue and *either* setComputedValuesAllRows or setComputedValuesFilteredRows
    
    /** Used to create a computed metric, when provided. If provided, the ComputedMetric class will define a setComputedValuesAllRows method using this formula
     * If it's not set, then you must define the getComputedValue method and *either* setComputedValuesAllRows *or* setComputedValuesFilteredRows, not both. Think of this as a completely manual metric */
    metricFormula?: string
    /** This value will be used when Metrics or Attributes not available in dataset */
    unresolvedValue?: any 

    // For Completely Manual Computed Metrics
    //getComputedValue?: (dataSet: IMetricDataSet, row: IdsRow) => any
    //setComputedValuesAllRows?: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  allRows: IdsRow[])=> Promise<void>
    //setComputedValuesFilteredRows?: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[], filteredRows: IdsRow[], allRows: IdsRow[])=> Promise<void>
}

export class ComputedMetric
    implements IdsMetric
{
    private _options: IComputedMetricCreateOptions
    private _Id: string
    private _Name: string
    private _ShortName: string
    private _SortOrder: number
    private _Hidden: boolean
    private _Format: string
    private _Currency: boolean
    private _AxisType: MetricAxisType
    readonly _rowAggType?: MetricRowAggType
    readonly _rowAggWeightMetricId?: string
    private _storeKey: string
    private _metricFormula: string
    private _metricFunction: EvalFunction
    private _errorCount: number
    private _getComputedValue: (dataSet: IMetricDataSet, row: IdsRow) => any
    private _setComputedValuesAllRows: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  allRows: IdsRow[]) => Promise<void>
    private _setComputedValuesFilteredRows: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  filteredRows: IdsRow[], allRows: IdsRow[]) => Promise<void>
    private _requiredMetricsIds: string[]
    private _requiredAttributeIds: string[]
    private _aggregateExpression: string

    constructor(settings: IdsMetric, options: IComputedMetricCreateOptions){
        this._options = options || {};
        this._Id = settings.Id
        this._Name = settings.Name
        this._ShortName = settings.ShortName
        this._SortOrder = settings.SortOrder
        this._Hidden = !!settings.Hidden
        this._Format = settings.format
        this._Currency = !!settings.currency
        this._AxisType = settings.axisType
        this._rowAggType = settings.rowAggType
        this._rowAggWeightMetricId = settings.rowAggWeightMetricId
        this._errorCount = 0
        this._storeKey = `computedMtr_${this._Id}`
        this._aggregateExpression = settings.aggregateExpression

        // inspect options and set computed metric properties
        this.setupComputedMetric()
    }

    get Id(): string { return this._Id }
    get Name(): string { return this._Name }
    get ShortName(): string { return this._ShortName }
    get SortOrder(): number { return this._SortOrder }
    get Hidden(): boolean { return this._Hidden }

    get axisType(): MetricAxisType { return this._AxisType }
    get aggregateExpression(): string { return this._aggregateExpression }
    get rowAggType(): MetricRowAggType { return this._rowAggType }
    get rowAggWeightMetricId(): string { return this._rowAggWeightMetricId }
    get format(): string { return this._Format; }
    get currency(): boolean { return this._Currency}

    get bMetricComputed(): boolean { return true}
    getComputedValue(dataSet: IMetricDataSet, row: IdsRow): any {
        return this._getComputedValue(dataSet, row);
    }

    // detailed docs are on IdsMetric interface
    get setComputedValuesAllRows(): (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  rows: IdsRow[]) => Promise<void> {
        // when this class is instantiated, the below property or the flitered one will be defined, not both. this class's constructor decides which one
        return this._setComputedValuesAllRows
    }
    // detailed docs are on IdsMetric interface
    get setComputedValuesFilteredRows(): (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  filteredRows: IdsRow[], allRows: IdsRow[]) => Promise<void> {
        return this._setComputedValuesFilteredRows
    }

    get requiredAttributeIds(): string[] { return this._requiredAttributeIds }
    get requiredMetricsIds(): string[] { return this._requiredMetricsIds }


    /** If this is a expression metric, it will define the required functions for your
     * If it's not, then you must provide the getComputedValue and either the setComputedValuesAllRows or the setComputedValuesFilteredRows functions */
    private setupComputedMetric(): void {
        const self = this
        let opts = this._options

        this._getComputedValue = (dataSet: IMetricDataSet, row: IdsRow) => {
            if (_.isEmpty(dataSet))
                return undefined;
            return dataSet.getRowData(row, self._storeKey);
        };

        // Setup Evaluation Function
        self._metricFormula = opts?.metricFormula || ''
        let dctSymbols: {[id:string]: string} = {}
        let parsedFormula = parse(this._metricFormula)
        parsedFormula.traverse((node: MathNode & SymbolNode,path, parent) => {
            if(node.type == "SymbolNode")
                dctSymbols[node.name] = node.name
        })
        self._requiredMetricsIds = opts.requiredMetricsIds || []
        self._requiredAttributeIds = opts.requiredAttributeIds || []

        // If not in existing require arrays, add to metrics as default
        _.forEach(_.values(dctSymbols), (aSymbolName) => {
            if(_.includes(self._requiredMetricsIds, aSymbolName)
              || _.includes(self._requiredAttributeIds, aSymbolName))
                return
            self._requiredAttributeIds.push(aSymbolName)
        })
        self._metricFunction = parsedFormula.compile()

        this._setComputedValuesAllRows = (async function(dataSet: IMetricDataSet,
                                                         rowAttributeIds: string[],
                                                         rowMetricIds: string[],
                                                         allRows: IdsRow[]): Promise<void> {
            if(_.isEmpty(dataSet))
                return;

            let missingAttr = _.difference(self.requiredAttributeIds, rowAttributeIds);
            let missingMtrc = _.difference(self.requiredMetricsIds, rowMetricIds);
            if (missingAttr.length || missingMtrc.length) {
                // Set to default value ?
                if (!_.isUndefined(opts.unresolvedValue)) {
                    _.forEach(allRows, (row) => {
                        dataSet.setRowData(row, self._storeKey, opts.unresolvedValue);
                    });
                }
                return;
            }

            for(let row of allRows) {
                let scope: any = {}
                try {
                    _.forEach(self._requiredAttributeIds, (attrId) => {
                        scope[attrId] = dataSet.getRowAttribute(row, attrId);
                    })
                    _.forEach(self._requiredMetricsIds, (metricId) => {
                        scope[metricId] = dataSet.getRowMetric(row, metricId);
                    })

                    const metricValue = self._metricFunction.evaluate(scope)
                    dataSet.setRowData(row, self._storeKey, metricValue)
                } catch (e) {
                    self._errorCount++
                }
            }



        })



    }


}
